/**
 * An organization's own secrets (§5.25): a value defined once for the whole
 * organization — a registry token, a shared staging password — that every
 * member's machine with a seat receives and every session of the
 * organization's workspaces is given as an environment variable.
 *
 * End-to-end like a workspace's (`e2e.ts`): sealed on an owner's or an admin's
 * machine under the organization key, kept by the Worker as ciphertext, opened
 * on each member's machine. They exist only for an organization that switched;
 * before the switch there is no key to seal them with and the routes say so.
 *
 * **Precedence.** A workspace defines its own environment, and that wins: a
 * repository variable of the same name — secret or not — is the more specific
 * statement, exactly as a repository's own variable wins over a host service's
 * (§5.9). An organization secret fills a name the workspace left empty.
 *
 * **Reach.** Every member, or the members of a list of teams. A team limits who
 * *receives* the value — the Worker hands its ciphertext only to their machines —
 * and whoever receives it has it in every organization workspace they open.
 * That is access control by the Worker, not by the key: every keyed machine
 * holds the one organization key, so the limit is as good as the Worker's word,
 * which is why it is said on the page.
 */

/** How many one organization keeps — the same ceiling as a workspace's. */
export const MAX_ORG_SECRETS = 200;

/** An environment variable's name, as a shell accepts it. */
export const ORG_SECRET_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/;

/** Who receives one: everybody, or the members of these teams. */
export interface OrgSecretReach {
  /** `null` is every member; a list is the members of those teams. */
  teams: string[] | null;
}

/** One organization secret, as the page and the app list it — never its value. */
export interface OrgSecretPresence {
  name: string;
  reach: OrgSecretReach;
  updatedAt: string;
  updatedBy: { id: string | null; label: string };
}

/** `GET /api/cloud/orgs/:id/org-secrets` and `GET /api/orgs/:id/org-secrets`. */
export interface OrgSecretList {
  /** Whether the organization can hold any: it switched to end-to-end encryption. */
  available: boolean;
  generation: number;
  secrets: OrgSecretPresence[];
}

/** `GET /api/cloud/orgs/:id/org-secrets/values` — the ones this member receives, sealed. Audited. */
export interface OrgSecretValues {
  generation: number;
  secrets: Record<string, { sealed: string; updatedAt: string }>;
}

/**
 * `POST /api/cloud/orgs/:id/org-secrets` — an owner's or an admin's machine
 * setting, re-scoping or removing some. `sealed` absent on a `set` keeps the
 * value and changes only who receives it.
 */
export interface OrgSecretsWrite {
  generation: number;
  set?: Record<string, { sealed?: string; reach: OrgSecretReach }>;
  remove?: string[];
}

/** A name an organization secret may have, or why not. */
export function orgSecretNameProblem(name: unknown): string | null {
  if (typeof name !== 'string' || !ORG_SECRET_NAME.test(name)) {
    return 'A name is a letter or underscore, then letters, digits and underscores — at most 128.';
  }
  return null;
}

/** A reach as it may be kept: `null`, or a list of distinct team ids. */
export function parseReach(value: unknown): OrgSecretReach | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const teams = (value as { teams?: unknown }).teams;
  if (teams === null || teams === undefined) return { teams: null };
  if (!Array.isArray(teams) || teams.length === 0 || teams.length > 50) return null;
  if (!teams.every((team) => typeof team === 'string' && /^[\w-]{1,64}$/.test(team))) return null;
  return { teams: [...new Set(teams as string[])].sort() };
}

/** The audit's spelling of an organization secret: a path no workspace secret can take. */
export function orgSecretPath(name: string): string {
  return `org/${name}`;
}
